<template>
	<template v-if="postData && Object.keys(postData).length > 0">
		<!-- Meta tags -->
		<Title v-if="postData.name[locale]">{{ postData.name[locale] }}</Title>
		<Link v-if="globalConfig.isBlue" rel="canonical" :href="'https://dreamjobs.hu' + $route.fullPath" />
		<Link v-else rel="canonical" :href="'https://' + globalConfig.host + $route.fullPath" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		<Meta v-if="postData.lead[locale]" name="description" :content="postData.lead[locale]" />
		<Meta v-if="postData.name[locale]" property="og:title" :content="postData.name[locale]" />
		<Meta v-if="postData.lead[locale]" property="og:description" :content="postData.lead[locale]" />
		<Meta property="og:type" content="article" />
		<Meta property="og:url" :content="'https://' + globalConfig.host + $route.fullPath" />
		<Meta
			v-if="heroImage"
			property="og:image"
			:content="`https://res.cloudinary.com/djbs/image/upload/${heroImage.cloudinary}.jpg`"
		/>
		<Meta
			v-if="heroImage"
			property="og:image:secure_url"
			:content="`https://res.cloudinary.com/djbs/image/upload/${heroImage.cloudinary}.jpg`"
		/>
		<Meta v-if="heroImage" property="og:image:type" content="image/jpeg" />
		<Meta v-if="heroImage" property="og:image:alt" :content="postData.lead[locale]" />
		<Meta v-if="heroImage" property="og:image:height" :content="imageSize.height.toString()" />
		<Meta v-if="heroImage" property="og:image:width" :content="imageSize.width.toString()" />
	</template>
	<section class="mt-10 overflow-x-clip">
		<div class="flex flex-col w-full" v-if="postData && Object.keys(postData).length > 0">
			<div class="flex flex-col w-full max-w-dj-lg mx-auto px-10 md:w-[78%] md:max-w-[1320px] md:px-0">
				<div class="flex items-center order-1 md:order-none lg:-ml-[90px] mt-2 md:mt-0">
					<BaseButton @click="$router.push(`/${locale}/blog`)" square type="tertiary" class="text-xl">
						<BaseIcon icon="ic:outline-arrow-back" />
					</BaseButton>
					<span class="text-dj-gray ml-5 md:ml-dj-40">{{ $t("blogPage.backToBlog") }}</span>
				</div>
				<BaseCloudImage
					v-if="heroImage"
					:key="heroImage.cloudinary"
					ref="postCoverPic"
					class="mt-4 order-1 md:order-none"
					custom-class="rounded-big overflow-hidden w-full mx-auto"
					image-class="w-full h-auto"
					:public-id="heroImage.cloudinary"
					alt="post-cover"
					:transformation="[{ width: 1320 }]"
					@getUrl="imageUrl = $event"
					@getSize="imageSize = $event"
				/>
				<div v-else class="order-1 md:order-none bg-dj-gray w-full h-[220px] md:h-[480px]"></div>
				<div
					class="order-last mt-4 mb-8 flex w-full flex-col items-stretch gap-3 md:order-none md:my-9 md:mb-0 md:flex-row md:items-center md:gap-0"
				>
					<div class="flex items-center md:mr-auto">
						<BaseCloudImage
							v-if="postData.author?.avatar"
							:public-id="postData.author.avatar"
							:transformation="[{ width: 50, height: 50, crop: 'scale' }, { radius: 25 }]"
							alt="author-avatar"
						/>
						<div v-else class="grid place-items-center bg-gray-100 rounded-full w-[50px] h-[50px]">
							<BaseIcon
								color="#969BA5"
								class="text-[24px] w-[24px] h-[24px]"
								icon="icon-park-outline:user"
							/>
						</div>
						<span class="ml-5 font-medium text-sm text-dj-gray-dark">{{ authorName }}</span>
					</div>
					<div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-dj-gray md:contents">
						<div v-if="postData.reading_time" class="flex items-center">
							<BaseIcon icon="ic:outline-access-time" color="currentColor" class="text-xl" />
							<span class="text-sm ml-3">{{ postData.reading_time }} {{ $t("blogPage.minutes") }}</span>
						</div>
						<span class="whitespace-nowrap text-sm md:ml-11">{{ prettifyDate() }}</span>
					</div>
					<div class="flex flex-wrap items-center gap-2.5 md:ml-11">
						<BaseButton
							@click="$FB.ui({ method: 'share', href: `https://${globalConfig.host + $route.fullPath}` })"
							class="shrink-0"
							type="tertiary"
						>
							<BaseIcon icon="ic:outline-facebook" color="#3A599B" class="text-2xl" />
							<span class="ml-4">{{ $t("blogPage.share") }}</span>
						</BaseButton>
						<BaseButton type="tertiary" class="shrink-0">
							<ClientOnly>
								<ShareNetwork
									network="linkedin"
									url="{{`https://${globalConfig.host + route.fullPath}`}}"
								>
									<div class="flex flex-wrap items-center">
										<div class="flex flex-col">
											<BaseIcon class="text-2xl" icon="mdi:linkedin" color="#0666C3" />
										</div>
										<div class="flex flex-col pl-[10px]">
											<span>LinkedIn</span>
										</div>
									</div>
								</ShareNetwork>
							</ClientOnly>
						</BaseButton>
					</div>
				</div>
				<div class="order-1 md:order-none">
					<span class="block text-sm md:text-xl text-dj-gray mt-5 md:mt-0">
						{{ postData.category.name[locale] }}
					</span>
					<div
						v-if="postData.name_html?.[locale]"
						class="post-rich-text post-rich-title mt-2 md:mt-0"
						v-html="postData.name_html[locale]"
					></div>
					<h1 v-else class="text-xl md:text-5xl md:leading-[75px] font-medium mt-2 md:mt-0">
						{{ postData.name[locale] }}
					</h1>
					<div
						v-if="postData.lead_html?.[locale]"
						class="prose max-w-full mt-2 md:mt-8 mb-0 md:mb-dj-30"
						v-html="postData.lead_html[locale]"
					></div>
					<div v-else-if="postData.lead?.[locale]" class="prose max-w-full mt-2 md:mt-8 mb-0 md:mb-dj-30">
						<p>{{ postData.lead[locale] }}</p>
					</div>
				</div>
				<div class="h-[1px] order-4 bg-dj-gray-light w-full md:hidden my-7"></div>
				<article
					v-if="postData.content[locale]"
					class="post-content prose max-w-full order-3 md:order-none mb-dj-30"
					v-html="postData.content[locale]"
				></article>
			</div>
			<div
				class="w-full pt-dj-50 bg-gradient-to-b from-[#F4F5FB] to-white"
				v-if="Object.keys(postData).length > 0"
			>
				<AdaptiveContainer>
					<h3 class="font-medium text-xl mb-7">{{ $t("blogPage.relatedArticles") }}</h3>
				</AdaptiveContainer>
				<AdaptiveContainer v-if="Object.keys(relatedArticles).length > 0">
					<DjCardBlog
						@on-card-click="$router.push(`/${locale}/blog/post/${article.slug}`)"
						@on-category-click="$router.push(`/${locale}/blog?category=${article.category.id}`)"
						v-for="(article, index) in relatedArticles"
						:key="index"
						:category="article.category.name[locale]"
						:public-id="article.image.cloudinary"
						:title="article.name[locale]"
						:cta="$t('blogPage.readMore')"
					/>
				</AdaptiveContainer>
			</div>
			<JobsPageCarousel
				:title="$t('blogPage.relatedJobs')"
				:raw-jobs-data="postData.jobs"
				hide-all-jobs
				carouselType="related-jobs"
			/>
			<template v-if="Object.keys(postData).length > 0 && postData.companies.length > 0">
				<div class="w-full">
					<AdaptiveContainer>
						<div class="flex items-baseline col-span-full mt-4 sm:mt-16 mb-8">
							<BaseHeading
								type="h5"
								class="mt-5 mb-2 relative before:content-['&nbsp;'] before:block before:absolute before:-bottom-1 before:h-1 before:w-20 before:bg-red-500"
							>
								{{ $t("blogPage.relatedCompanies") }}
							</BaseHeading>
						</div>
					</AdaptiveContainer>
				</div>
				<client-only>
					<CarouselCompanies
						v-if="loadingBanner == false"
						ref="carouselCompaniesRef"
						@loaded="loading = false"
						:slideWidth="320"
						:rawCompaniesData="postData.companies"
						:showBanner="!isMobileScreen() && Object.keys(bannerRelatedCompanies).length > 0"
						:banner="bannerRelatedCompanies"
					/>
				</client-only>
				<!-- Loading State -->
				<AdaptiveContainer v-if="loading">
					<div class="block w-[1000vw] mt-6 mb-12">
						<div v-for="(item, index) in 5" :key="index" class="inline-block float-left w-[320px] mr-10">
							<DjCardCompany :loading="true" />
						</div>
					</div>
				</AdaptiveContainer>
				<!-- Mobile Banner for Carousel -->
				<AdaptiveContainer v-if="Object.keys(bannerRelatedCompanies).length > 0 && isMobileScreen() == true">
					<DjCardCompanyBanner
						:subtitle="bannerRelatedCompanies.sub_title[locale]"
						:title="bannerRelatedCompanies.title[locale]"
						:description="bannerRelatedCompanies.content[locale]"
						:cta="bannerRelatedCompanies.cta_text[locale]"
						:url="bannerRelatedCompanies.cta_link[locale]"
						:logo="bannerRelatedCompanies.image.cloudinary"
						class="mt-5 mb-10"
					/>
				</AdaptiveContainer>
			</template>
		</div>
		<div class="flex flex-col items-center py-4" v-else>
			<h1 class="text-4xl font-medium">{{ $t("blogPage.notFound") }}</h1>
			<BaseCloudImage
				class="max-w-[260px] sm:max-w-full"
				public-id="content/DreamJobs/Graphics/pic_404"
				postfix=".svg"
				alt="404"
			/>
			<BaseButton @click="$router.push(`/${locale}/blog`)" class="flex items-center text-white">
				<BaseIcon class="text-xl mr-4" icon="ic:outline-arrow-back" color="currentColor" />
				<span>{{ $t("blogPage.backToBlog") }}</span>
			</BaseButton>
		</div>
	</section>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";
import { type bannerObject } from "@@/types";
import { getRegionCode, axiosErrorHandler, constructApiUrl } from "@@/composables/helpers";

export default defineComponent({
	name: "BlogPostPage",
	async setup() {
		const route = useRoute();
		const region = ref("HU");
		const { locale } = useI18n();

		const loading = ref(true);
		const loadingBanner = ref(true);

		const postData = ref();
		const relatedArticles = ref([]);
		const imageUrl = ref("");
		// Language specific hero image; follows the language switcher without refetching
		const heroImage = computed(
			() => postData.value?.locale_images?.[locale.value] ?? postData.value?.image ?? null
		);
		const postCoverPic = ref<HTMLElement>();

		const authorName = ref("");

		const carouselCompaniesRef = ref(null);
		const apiUrl = ref(globalConfig.API);
		const isBlue = ref(globalConfig.isBlue);
		const isNotBlueHU = ref(globalConfig.isNotBlueHU);

		const bannerRelatedCompanies = ref({} as bannerObject);

		const isMobileScreen = () => {
			return carouselCompaniesRef.value?.isMobile();
		};

		onMounted(async () => {
			window.scrollTo(0, 0);

			requestBannerRelatedCompanies().then(() => {
				loadingBanner.value = false;
			});

			region.value = useRegion.getCurrentRegion().code;
		});

		const imageSize = ref({ height: 0, width: 0 });

		// Get article by slug
		await axios
			.get(
				apiUrl.value +
					`/posts/${route.params.blog_slug}` +
					(isBlue.value ? "?scope[]=isBlue" : isNotBlueHU.value ? "?scope[]=isNotBlue" : "")
			)
			.then((response) => {
				postData.value = response.data;
				authorName.value = response.data.author.vname + " " + response.data.author.kname;

				// Get related articles
				axios
					.get(
						constructApiUrl(
							`/posts?category=${response.data.category.id}&limit=4&region=${getRegionCode()}`
						).href
					)
					.then((response) => {
						relatedArticles.value = response.data.data.filter((article) => article.id != postData.value.id);
					})
					.catch((error) => {
						axiosErrorHandler("blog_slug categores error", error);
					});

				loading.value = false;
			})
			.catch((error) => {
				axiosErrorHandler("blog_slug posts error", error);
			});

		function prettifyDate() {
			const blogDate =
				postData.value.onair && postData.value.onair[region.value]
					? postData.value.onair[region.value]
					: postData.value.created_at;

			return new Intl.DateTimeFormat(locale.value, {
				month: "long",
				day: "2-digit",
			}).format(new Date(blogDate.replace(" ", "T")));
		}

		async function requestBannerRelatedCompanies() {
			bannerRelatedCompanies.value = await useBanners.getBanner("related-companies");
		}

		return {
			loading,
			loadingBanner,
			postData,
			prettifyDate,
			locale,
			relatedArticles,
			authorName,
			imageUrl,
			heroImage,
			imageSize,
			postCoverPic,
			bannerRelatedCompanies,
			carouselCompaniesRef,
			isMobileScreen,
		};
	},
});
</script>
<style lang="scss">
.post-rich-text {
	p {
		margin: 0;
	}

	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		font-weight: 500;
		line-height: 1.25;
		margin: 0.3em 0;
	}

	h1 {
		font-size: 1.875rem;
	}

	h2 {
		font-size: 1.5rem;
	}

	h3 {
		font-size: 1.25rem;
	}

	h4 {
		font-size: 1.125rem;
	}

	h5,
	h6 {
		font-size: 1rem;
	}

	a {
		text-decoration: underline;
	}

	img,
	iframe,
	video {
		max-width: 100%;
		height: auto;
	}
}

.post-content {
	img {
		max-width: 100%;
		height: auto !important;
	}

	// Editor image alignment: the image sits on its own line, text never wraps beside it
	img.left,
	img[align="left"],
	img[style*="float: left"],
	img[style*="float:left"],
	img.right,
	img[align="right"],
	img[style*="float: right"],
	img[style*="float:right"],
	img.center,
	img[align="center"],
	img[style*="margin-left: auto"],
	img[style*="margin-left:auto"] {
		display: block;
		float: none !important;
		margin-top: 1rem;
		margin-bottom: 1rem;
	}

	img.left,
	img[align="left"],
	img[style*="float: left"],
	img[style*="float:left"] {
		margin-left: 0;
		margin-right: auto;
	}

	img.right,
	img[align="right"],
	img[style*="float: right"],
	img[style*="float:right"] {
		margin-left: auto;
		margin-right: 0;
	}

	img.center,
	img[align="center"],
	img[style*="margin-left: auto"],
	img[style*="margin-left:auto"] {
		margin-left: auto;
		margin-right: auto;
	}

	// image2 puts the alignment class on the paragraph wrapping the image
	p.left,
	p.center,
	p.right {
		img {
			display: inline-block;
			float: none;
			margin: 0;
		}
	}

	// Edge to edge: breaks out of the centered text column to the full screen width
	p.full-width img,
	p img.full-width {
		display: block;
		width: 100vw;
		max-width: 100vw;
		margin-left: calc(50% - 50vw);
		margin-right: 0;
		border-radius: 0;
	}

	// Text column width on large screens, edge to edge on mobile
	p img.full-mobile {
		display: block;
		width: 100%;
		max-width: 100%;
		margin-left: 0;
		margin-right: 0;
	}

	@media (max-width: 767px) {
		p img.full-mobile {
			width: 100vw;
			max-width: 100vw;
			margin-left: calc(50% - 50vw);
			border-radius: 0;
		}
	}

	// Image on the left with the text running beside it
	p img.wrap-left {
		display: block;
		float: left;
		max-width: 50%;
		margin: 0.25rem 1.5rem 1rem 0;
	}

	@media (max-width: 767px) {
		p img.wrap-left {
			float: none;
			max-width: 100%;
			margin: 1rem auto;
		}
	}

	p.left {
		text-align: left;
	}

	p.center {
		text-align: center;
	}

	p.right {
		text-align: right;
	}

	// Paragraph alignment from the editor's justify buttons
	p[style*="text-align: center"] img,
	p[style*="text-align:center"] img {
		display: inline-block;
	}
}

.post-rich-title > p {
	font-size: 1.25rem;
	line-height: 1.35;
	font-weight: 500;
}

@media (min-width: theme("screens.md")) {
	.post-rich-title > p,
	.post-rich-text h1 {
		font-size: 3rem;
		line-height: 75px;
	}

	.post-rich-text h2 {
		font-size: 1.5rem;
	}
}

@media (max-width: theme("screens.md")) {
	.prose {
		img {
			width: 100% !important;
			max-width: 768px;
			height: auto !important;
		}
	}
}

// Images flagged in the dj admin editor ("Full width image"): break out of the centered column
.prose img.img-fullwidth {
	display: block;
	width: 100vw !important;
	max-width: none !important;
	height: auto !important;
	margin-left: calc(50% - 50vw);
	margin-right: 0;
	border-radius: 0;
}
</style>
